// @ts-nocheck
/**
 * Shared LocalizedText resolution for templates and category bundles.
 */

import type { LocalizedText } from '../v2/types/metadata';

export const DEFAULT_LOCALE = 'en';

const DEFAULT_FALLBACK_LOCALES = [DEFAULT_LOCALE, 'en-US', 'en_GB'];

export interface ResolveLocalizedTextOptions {
  fallbackLocales?: readonly string[];
  fallback?: string;
}

/** Normalize a plain string or locale map into a locale-keyed record. */
export function normalizeLocalizedText(value: LocalizedText | undefined): Record<string, string> {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? { [DEFAULT_LOCALE]: trimmed } : {};
  }
  const result: Record<string, string> = {};
  for (const [locale, text] of Object.entries(value)) {
    if (typeof text === 'string' && text.trim().length > 0) {
      result[locale] = text;
    }
  }
  return result;
}

/** Merge locale entries; later maps override earlier ones for the same locale key. */
export function mergeLocalizedText(
  ...values: Array<LocalizedText | undefined>
): Record<string, string> {
  const merged: Record<string, string> = {};
  for (const value of values) {
    Object.assign(merged, normalizeLocalizedText(value));
  }
  return merged;
}

/** Coerce an imported value into LocalizedText, wrapping bare strings under `en`. */
export function coerceToLocalizedText(value: unknown): LocalizedText | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const map = value as Record<string, unknown>;
    const entries = Object.entries(map).filter(
      ([, text]) => typeof text === 'string' && text.trim().length > 0,
    ) as Array<[string, string]>;
    if (entries.length === 0) {
      return undefined;
    }
    if (entries.length === 1 && entries[0][0] === DEFAULT_LOCALE) {
      return entries[0][1];
    }
    return Object.fromEntries(entries);
  }
  return undefined;
}

/** Resolve display text for the requested locale with standard fallbacks. */
export function resolveLocalizedText(
  value: LocalizedText | undefined,
  locale: string,
  options?: ResolveLocalizedTextOptions,
): string {
  const map = normalizeLocalizedText(value);
  const fallbackLocales = options?.fallbackLocales ?? DEFAULT_FALLBACK_LOCALES;

  const candidates = [locale, ...fallbackLocales];
  for (const candidate of candidates) {
    const text = map[candidate];
    if (typeof text === 'string' && text.trim().length > 0) {
      return text;
    }
  }

  const firstAvailable = Object.values(map).find((text) => text.trim().length > 0);
  if (firstAvailable) {
    return firstAvailable;
  }

  return options?.fallback ?? '';
}

/** Prefix/suffix chrome: keep leading/trailing spaces that name maps would trim. */
export function resolveLocalizedAffix(value: LocalizedText | undefined, locale: string): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  const map = value as Record<string, string>;
  const candidates = [locale, DEFAULT_LOCALE, 'en-US', 'en_GB', ...Object.keys(map)];
  for (const candidate of candidates) {
    const text = map[candidate];
    if (typeof text === 'string' && text.length > 0) return text;
  }
  return '';
}

/** Collect distinct locale keys present in a LocalizedText value. */
export function getLocalizedTextLocales(value: LocalizedText | undefined): string[] {
  return Object.keys(normalizeLocalizedText(value)).sort();
}

/** Count distinct locales across many LocalizedText values. */
export function countDistinctLocales(...values: Array<LocalizedText | undefined>): number {
  const locales = new Set<string>();
  for (const value of values) {
    for (const locale of getLocalizedTextLocales(value)) {
      locales.add(locale);
    }
  }
  return locales.size;
}
